import { isEqual } from "lodash";
import { useCallback, useEffect, useReducer, useRef } from "react";
import { cleanIsEqual } from "./cleanIsEqual";
import { debounceStackReducer } from "./debounceStackReducer";
import {
  CLEAR_ON_CHANGE_PAYLOAD,
  FORCE_WITH_VALUE,
  RESET_FLAGS,
  SET_VALUE,
  SYNC_FROM_PARENT,
  UPDATE_INDEX,
  UseDebounceStackProps,
  UseDebounceStackReturn,
} from "./interface";

/**
 * A custom React hook that manages a debounced, undoable stack of values.
 *
 * This hook is useful for scenarios where you want to debounce updates to a value,
 * maintain a history stack for undo/redo functionality, and synchronize changes
 * with a parent state or callback.
 *
 * @template T The type of the value being managed.
 * @param props - Destructured parameter object
 * @param props.value - Initial value to populate the history stack with.
 * @param props.setValue - Callback to apply the debounced value back to the parent state.
 * @param props.debounceMilliseconds - Milliseconds to debounce updates (null for immediate, negative to only update on forced updates).
 * @param props.onChange - Optional callback invoked when the value changes; receives the new value, the current index, and the stack.
 * @param props.comparisonFunction - Optional function used to compare values for stack deduplication; defaults to deep equality.
 * @param props.blockReturnFunction - Optional guard that prevents `setValue` from being called when returning from external updates.
 * @param props.blockOnChange - Optional guard that prevents `onChange` being triggered for top-down synchronization changes.
 * @returns An object with the following properties:
 *          - `currentValue` - The current (possibly debounced) value from the stack.
 *          - `setCurrentValue` - Function to set the current value (debounced).
 *          - `forceUpdate` - Function to immediately update the value, bypassing debounce.
 *          - `undo` - Function to revert to a previous value in the stack.
 *          - `redo` - Function to advance to a more recent value in the stack.
 *          - `stack` - The full readonly stack of values.
 *          - `index` - The current numeric index pointing at the active item in the stack.
 */
export const useDebounceStack = <T>({
  value,
  setValue,
  debounceMilliseconds = 500,
  onChange = () => {},
  comparisonFunction = isEqual,
  blockReturnFunction,
  blockOnChange,
}: UseDebounceStackProps<T>): UseDebounceStackReturn<T> => {
  const [state, dispatch] = useReducer(debounceStackReducer<T>, {
    stack: [value],
    index: 0,
    changed: false,
    comparisonChanged: false,
    onChangePayload: null,
  });
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * Set the current value in the stack, updating the stack and index if necessary
   * @param newValue The new value to set as current
   * @returns void
   */
  const setCurrentValue = useCallback(
    (newValue: T) => {
      // There will always be an update if the value is different
      if (!cleanIsEqual(newValue, state.stack[state.index])) {
        // Determine if comparison fails (values are different)
        const addToStack = !comparisonFunction(state.stack[state.index], newValue);
        // Dispatch the update (onChange will be triggered by useEffect)
        dispatch({
          operation: SET_VALUE,
          payload: { newValue, addToStack },
        });
      }
    },
    [state.index, state.stack, comparisonFunction],
  );

  /**
   * Undo the last change by moving back in the stack
   * @param steps Number of steps to go back, defaults to 1
   * @returns void
   */
  const undo = useCallback(
    (steps = 1) => {
      const newIndex = Math.max(0, state.index - steps);
      dispatch({ operation: UPDATE_INDEX, payload: { index: newIndex } });
    },
    [state.index],
  );

  /**
   * Redo the last undone change by moving forward in the stack
   * @param steps Number of steps to go forward, defaults to 1
   * @returns void
   */
  const redo = useCallback(
    (steps = 1) => {
      const newIndex = Math.min(state.stack.length - 1, state.index + steps);
      dispatch({ operation: UPDATE_INDEX, payload: { index: newIndex } });
    },
    [state.index, state.stack],
  );

  /**
   * Force an update to the current value, bypassing debounce
   * @param newValue Optional new value to set, if provided it will update the stack
   * @returns void
   */
  const forceUpdate = useCallback(
    (newValue?: T) => {
      // Check for a timer and clear it
      const waitingTimer = debounceTimer.current !== null;
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }

      // Determine the value to pass to setValue
      let valueToSet = state.stack[state.index];

      // Check if new value is provided and update the stack accordingly
      /* istanbul ignore else */
      if (newValue !== undefined && !cleanIsEqual(newValue, state.stack[state.index])) {
        // Determine if comparison fails (values are different)
        const addToStack = !comparisonFunction(state.stack[state.index], newValue);
        // Determine if onChange should be triggered
        const triggerOnChange = addToStack || waitingTimer || debounceMilliseconds === -1;
        // Dispatch the update (all stack manipulation happens in reducer)
        dispatch({
          operation: FORCE_WITH_VALUE,
          payload: { newValue, addToStack, triggerOnChange },
        });
        // Use the new value for setValue
        valueToSet = newValue;
      }

      // Push update to parent for the current value
      setValue(valueToSet);
      if (newValue === undefined) {
        dispatch({ operation: RESET_FLAGS });
      }
    },
    [debounceMilliseconds, state.index, state.stack, setValue, comparisonFunction],
  );

  /**
   * Effect to handle changes in the stack and current value
   * This effect will run when the value changes, the stack changes, or the index changes
   * It will update the current value based on the stack and debounce settings
   * If the value is changed via setCurrentValue, it will not trigger a top-down change
   * If the value is changed from the top down, it will update the current value
   * If debounce is set, it will wait for the specified time before updating the value
   * If debounce is null, it will update the value immediately
   * If debounce is negative, it will only update the value when forceUpdate is called
   * If the value is the same as the current value, it will reset the changed state
   * @returns void
   */
  // Effect to handle onChange callbacks
  useEffect(() => {
    if (state.onChangePayload) {
      onChange(
        state.onChangePayload.value,
        state.onChangePayload.index,
        state.onChangePayload.stack,
      );
      dispatch({ operation: CLEAR_ON_CHANGE_PAYLOAD });
    }
  }, [state.onChangePayload, onChange]);

  useEffect(() => {
    const thisCurrentValue = state.stack[state.index];
    // If no change via setCurrentValue, must be a top down change
    if (!state.changed && !cleanIsEqual(value, thisCurrentValue)) {
      // Determine if comparison fails (values are different)
      const addToStack = !comparisonFunction(state.stack[state.index], value);
      // Clear timer
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      // Determine if onChange should be triggered
      const triggerOnChange = !blockOnChange?.(thisCurrentValue, value);
      // Dispatch the update
      dispatch({
        operation: SYNC_FROM_PARENT,
        payload: { newValue: value, addToStack, triggerOnChange },
      });
    }
    // Reset the changed state if the value is the same
    else if (state.changed && cleanIsEqual(value, thisCurrentValue)) {
      /* istanbul ignore else */
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      dispatch({ operation: RESET_FLAGS });
    }

    // Restart timeout if debounce is set and there is a change
    else if (
      debounceMilliseconds !== null &&
      debounceMilliseconds >= 0 &&
      state.comparisonChanged
    ) {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      if (!blockReturnFunction?.(thisCurrentValue))
        debounceTimer.current = setTimeout(() => {
          setValue(thisCurrentValue);
          dispatch({ operation: RESET_FLAGS });
        }, debounceMilliseconds);
      // Do not add a return function to clear the timer here,
      // as it would clear the timer on every render, which is not desired.
      // The time will be cleared in the other scenario handlers or should be allowed to run
      // return () => {
      //   if (debounceTimer.current) clearTimeout(debounceTimer.current);
      // };
    }
    // If debounce is null set the value immediately.
    // If deboubce is negative, it will only update on forceUpdate
    else if (
      debounceMilliseconds === null &&
      state.comparisonChanged &&
      !blockReturnFunction?.(thisCurrentValue)
    ) {
      setValue(thisCurrentValue);
      dispatch({ operation: RESET_FLAGS });
    }
  }, [
    blockOnChange,
    blockReturnFunction,
    state.changed,
    state.comparisonChanged,
    state.index,
    state.stack,
    debounceMilliseconds,
    setValue,
    comparisonFunction,
    value,
  ]);

  return {
    currentValue: state.stack[state.index],
    setCurrentValue,
    forceUpdate,
    undo,
    redo,
    stack: state.stack,
    index: state.index,
  };
};
